import React, { useState, useRef, useEffect } from 'react';
import './GCodePreviewCard.css';
import { formatSize, formatPrintTime, extractGCodeInfo } from '../utils/gcodeUtils';
import { GCodeVisualizer } from './GCodeVisualizer';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { AnonymousLoginModal } from './AnonymousLoginModal';
import { isAnonymousUser } from '../utils/anonymousSession';
import { api } from '../services/api';

export interface GCodeFile {
  id?: string;
  filename: string;
  size: number;
  plateNumber: number; // frontend format
  plate_number?: number; // API format
  download_url: string;
  storage_path?: string;
  content?: string;
}

interface GCodePreviewCardProps {
  file: GCodeFile;
  isSelected?: boolean;
  onToggleSelect?: () => void;
}

interface GCodeInfo {
  estimatedTime: number | null;
  filamentLength: number | null;
  filamentWeight: number | null;
  layerCount: number | null;
}

interface BedDimensions {
  width: number;
  height: number;
  source: 'metadata' | 'gcode_parsing' | 'fallback';
}

export function GCodePreviewCard({ file, isSelected, onToggleSelect }: GCodePreviewCardProps) {
  const { t } = useTranslation();
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [content, setContent] = useState<string | null>(null);
  const [gcodeInfo, setGcodeInfo] = useState<GCodeInfo | null>(null);
  const [bedDimensions, setBedDimensions] = useState<BedDimensions | null>(null);
  const [showAnonymousModal, setShowAnonymousModal] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  // Get bed dimensions from metadata (preferred) or G-code parsing (fallback)
  const getBedDimensionsFromContent = async (gcodeContent?: string): Promise<BedDimensions | null> => {
    console.log(`[BED DIMENSIONS DEBUG] Starting getBedDimensions for file:`, file.filename);
    console.log(`[BED DIMENSIONS DEBUG] File storage_path:`, file.storage_path);
    console.log(`[BED DIMENSIONS DEBUG] Content available:`, !!gcodeContent);
    
    // First try to get dimensions from metadata if we have a storage path
    if (file.storage_path) {
      try {
        console.log(`[BED DIMENSIONS DEBUG] Attempting to get bed dimensions from metadata for: ${file.storage_path}`);
        const metadataResult = await api.gcode.getBedDimensionsFromMetadata(file.storage_path);
        console.log(`[BED DIMENSIONS DEBUG] ✅ Got bed dimensions from metadata:`, metadataResult);
        return {
          width: metadataResult.width,
          height: metadataResult.height,
          source: 'metadata'
        };
      } catch (error) {
        console.warn(`[BED DIMENSIONS DEBUG] ❌ Failed to get bed dimensions from metadata, will try G-code parsing:`, error);
      }
    } else {
      console.log(`[BED DIMENSIONS DEBUG] No storage_path available, skipping metadata API`);
    }

    // Fallback to parsing G-code content if we have it
    if (gcodeContent) {
      try {
        console.log(`[BED DIMENSIONS DEBUG] 🔍 Attempting to parse bed dimensions from G-code content (${gcodeContent.length} chars)`);
        console.log(`[BED DIMENSIONS DEBUG] First 500 chars of G-code:`, gcodeContent.substring(0, 500));
        
        const { GCodeParser } = await import('../utils/gcodeParser');
        const parser = new GCodeParser();
        parser.parse(gcodeContent);
        const dimensions = parser.getBedDimensions();
        
        console.log(`[BED DIMENSIONS DEBUG] GCodeParser returned dimensions:`, dimensions);
        
        if (dimensions.x && dimensions.y) {
          console.log(`[BED DIMENSIONS DEBUG] ✅ Got bed dimensions from G-code parsing: ${dimensions.x}x${dimensions.y}`);
          return {
            width: dimensions.x,
            height: dimensions.y,
            source: 'gcode_parsing'
          };
        } else {
          console.warn(`[BED DIMENSIONS DEBUG] ❌ GCodeParser returned null dimensions`);
        }
      } catch (error) {
        console.error(`[BED DIMENSIONS DEBUG] ❌ Failed to parse bed dimensions from G-code content:`, error);
      }
    } else {
      console.log(`[BED DIMENSIONS DEBUG] No G-code content available for parsing`);
    }

    // No fallback - if we can't get correct dimensions, don't show misleading ones
    console.warn(`[BED DIMENSIONS DEBUG] ⚠️ Could not determine bed dimensions - no grid will be shown`);
    return null;
  };

  // Load GCode content when the component becomes visible
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        const wasVisible = isVisible;
        const isNowVisible = entry.isIntersecting;
        
        setIsVisible(isNowVisible);
        
        // If component was visible and now becomes invisible, reset loading state
        // This helps prevent stale content when folder is collapsed/expanded
        if (wasVisible && !isNowVisible) {
          setLoading(true);
          setError(null);
        }
      },
      { threshold: 0.1 } // 10% visibility triggers the callback
    );
    
    if (cardRef.current) {
      observer.observe(cardRef.current);
    }
    
    return () => {
      if (cardRef.current) {
        observer.unobserve(cardRef.current);
      }
    };
  }, [isVisible]);

  // Load GCode content directly from the file URL
  useEffect(() => {
    if (!isVisible) {
      // If not visible, skip loading
      return;
    }

    // If content already exists and no error, skip reloading
    if (content && !error) {
      return;
    }

    const fetchGCodeContent = async () => {
      try {
        setLoading(true);
        setError(null); // Clear any previous errors
        
        // Use file.content if it's already provided
        if (file.content) {
          setContent(file.content);
          // Extract GCode info
          const info = extractGCodeInfo(file.content);
          setGcodeInfo(info);
          
          // Get bed dimensions AFTER content is set (this allows G-code parsing to work)
          const dimensions = await getBedDimensionsFromContent(file.content);
          setBedDimensions(dimensions);
          
          setLoading(false);
          return;
        }
        
        // Fetch the content directly from the download URL
        // No need for auth headers since it's a signed URL
        console.log(`[DEBUG] Fetching G-code content from: ${file.download_url}`);
        const response = await fetch(file.download_url);
        if (!response.ok) {
          throw new Error(`Failed to fetch G-code file: ${response.statusText}`);
        }
        
        const gcodeContent = await response.text();
        setContent(gcodeContent);
        
        // Extract GCode info
        const info = extractGCodeInfo(gcodeContent);
        setGcodeInfo(info);
        
        // Get bed dimensions AFTER content is loaded (this allows G-code parsing to work)
        const dimensions = await getBedDimensionsFromContent(gcodeContent);
        setBedDimensions(dimensions);
        
        console.log(`[DEBUG] Successfully loaded ${gcodeContent.length} characters of G-code content`);
      } catch (err) {
        console.error('[DEBUG] Error loading G-code file:', err);
        setError(err instanceof Error ? err.message : 'Unknown error loading G-code file');
        setContent(null); // Clear any stale content
      } finally {
        setLoading(false);
      }
    };
    
    fetchGCodeContent();
  }, [isVisible, file.download_url, error]); // Remove content from deps to allow retry on error

  // Download the individual G-code file
  const downloadGCodeFile = async () => {
    // Check if user is anonymous - show login modal instead of downloading
    if (isAnonymousUser(currentUser)) {
      setShowAnonymousModal(true);
      return;
    }

    try {
      if (content) {
        // Create a blob from the content
        const blob = new Blob([content], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        
        // Create a download link
        const link = document.createElement('a');
        link.href = url;
        link.download = file.filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        // Clean up the URL
        URL.revokeObjectURL(url);
      } else {
        // If content is not loaded, fetch directly from the download URL
        console.log(`[DEBUG] Downloading G-code file from: ${file.download_url}`);
        
        // Create a download link using the direct URL
        const link = document.createElement('a');
        link.href = file.download_url;
        link.download = file.filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (err) {
      console.error('[DEBUG] Error downloading G-code file:', err);
      alert(t('gcodePreview.errors.downloadFailed', { 
        message: err instanceof Error ? err.message : 'Unknown error' 
      }));
    }
  };
  
  // Render a preview of the G-code content
  const renderPreview = () => {
    if (error) {
      return (
        <div className="gcode-preview-placeholder error">
          <p>{t('gcodePreview.error', { message: error })}</p>
        </div>
      );
    }
    
    if (loading) {
      return (
        <div className="gcode-preview-placeholder">
          <p>{t('gcodePreview.loading')}</p>
        </div>
      );
    }
    
    if (!content) {
      return (
        <div className="gcode-preview-placeholder">
          <p>{t('gcodePreview.noContent')}</p>
        </div>
      );
    }
    
    // Show 3D visualization
    return (
      <GCodeVisualizer 
        gcodeContent={content} 
        height={300} 
        bedDimensions={bedDimensions ? {
          width: bedDimensions.width,
          height: bedDimensions.height,
          source: bedDimensions.source
        } : undefined}
      />
    );
  };

  // Format filament weight with proper units
  const formatFilamentWeight = (weight: number | null): string => {
    if (weight === null || weight === undefined || weight < 0) return 'N/A';
    if (weight === 0) return '0g';
    if (weight < 1) {
      return `${(weight * 1000).toFixed(0)}mg`; // Convert small amounts to mg
    }
    return `${weight.toFixed(1)}g`;
  };

  return (
    <div 
      ref={cardRef} 
      className={`gcode-preview-card ${isSelected ? 'selected' : ''}`}
    >
      <div className="gcode-preview-container">
        {renderPreview()}
      </div>
      
      <div className="gcode-info">
        {gcodeInfo && (
          <div className="gcode-metadata-container">
            <div className="gcode-metadata-item">
              <span className="time-icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <polyline points="12 6 12 12 16 14"></polyline>
                </svg>
              </span>
              <span className="metadata-label">{t('gcodePreview.metadata.totalTime')}</span>
              <span className="metadata-value">{gcodeInfo.estimatedTime !== null ? formatPrintTime(gcodeInfo.estimatedTime) : 'N/A'}</span>
            </div>
            <span className="metadata-separator">|</span>
            <div className="gcode-metadata-item">
              <span className="filament-icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 2L2 7l10 5 10-5-10-5z"></path>
                  <path d="M2 17l10 5 10-5"></path>
                  <path d="M2 12l10 5 10-5"></path>
                </svg>
              </span>
              <span className="metadata-label">{t('gcodePreview.metadata.totalFilament')}</span>
              <span className="metadata-value">{formatFilamentWeight(gcodeInfo.filamentWeight)}</span>
            </div>
          </div>
        )}
        
        <div className="gcode-actions">
          <button 
            className="gcode-download-button" 
            onClick={downloadGCodeFile}
          >
            {t('gcodePreview.actions.download')}
          </button>
        </div>
      </div>

      {/* Anonymous Login Modal */}
      <AnonymousLoginModal
        isOpen={showAnonymousModal}
        onClose={() => setShowAnonymousModal(false)}
        downloadType="gcode"
        fileName={file.filename}
      />
    </div>
  );
} 